<template>
  <Teleport v-if="enableEdit" to="#bulk-actions">
    <BulkActions
      default-expanded
      :user-id="userId"
      :recipe-search-store="recipeSearchStore"
      :recipe-tag-store="recipeTagStore"
      :daily-values-options="dailyValuesOptions"
      :enable-edit="enableEdit"
      :enable-data-download="false"
      :delete-confirms="deleteConfirms"
    />
    <!-- ^ Note: On this page, we show the download data panel further below (below download labels),
           hence enable-data-download=false for BulkActions. -->
  </Teleport>

  <section class="mb-4 mobile-help">
    <span v-bind="$props" :content="props.content" tag="span" class="icon has-text-info no-print">
      <i class="fa fa-circle-info"></i>
    </span>
    <p class="is-block">
      To download labels or spreadsheets, select recipes then
      <a href="#download-panels">scroll down to continue</a>.
    </p>
  </section>

  <div class="columns is-multiline">
    <div class="column is-8-desktop is-12-tablet">
      <RecipeSelector
        :recipe-search-store="recipeSearchStore"
        :recipe-tag-store="recipeTagStore"
        :user-has-archived-recipes="userHasArchivedRecipes"
        selection-mode
        multiple-selection-mode
        show-modified
        always-show-modified-column
        :allow-inline-tag-edits="false"
        :show-recipe-actions-column="false"
        :enable-archiving="enableArchiving"
      />
    </div>

    <div id="download-panels" class="column is-4-desktop is-12-tablet order-0 has-text-centered">
      <!-- Download Labels box -->
      <div class="box mb-4">
        <p class="subtitle is-6 has-text-centered mb-4">{{ downloadLabelsTitle }}</p>

        <div class="field has-text-left">
          <label class="label" :for="fileFormatId">File Format:</label>
          <div class="control">
            <div class="select is-fullwidth">
              <select :id="fileFormatId" v-model="printDialogStore.fileFormat">
                <option value="pdf-pdf">Single PDF</option>
                <option value="zip-pdf">ZIP of PDFs</option>
                <option value="zip-png">ZIP of PNGs</option>
              </select>
            </div>
          </div>
        </div>

        <div v-if="printDialogStore.innerFormat === 'pdf'" class="field has-text-left">
          <label class="label" :for="pageSizeId">Page Size:</label>
          <div class="control">
            <div class="select is-fullwidth">
              <select :id="pageSizeId" v-model="printDialogStore.pageSize">
                <option value="auto">Crop to label</option>
                <option value="letter">Letter</option>
                <option value="legal">Legal</option>
                <option value="A4">A4</option>
              </select>
            </div>
          </div>
        </div>

        <div
          v-if="printDialogStore.innerFormat === 'pdf' && printDialogStore.pageSize !== 'auto'"
          class="field has-text-left"
        >
          <label class="label" :for="orientationId">Orientation:</label>
          <div class="control">
            <div class="select is-fullwidth">
              <select :id="orientationId" v-model="printDialogStore.orientation">
                <option value="portrait">Portrait</option>
                <option value="landscape">Landscape</option>
              </select>
            </div>
          </div>
        </div>

        <div v-if="printDialogStore.innerFormat === 'png'" class="field has-text-left">
          <label class="label" :for="zoomId">Zoom:</label>
          <div class="is-flex">
            <input
              :id="zoomId"
              v-model="printDialogStore.zoom"
              class="is-flex-grow-1 is-clickable"
              type="range"
              step="0.2"
              min="0.8"
              max="6.0"
            />
            <span class="has-text-right" style="padding-left: 1em; width: 3em">
              {{ printDialogStore.zoom }}x
            </span>
          </div>
        </div>

        <div v-if="downloadLabelsTooMany" class="field">
          <p class="help is-danger">
            A maximum of {{ max }} recipes can be downloaded at once. Please narrow your selection,
            or
            <a tabindex="0" href="" @click.prevent="truncateSelection"
              >click here to truncate your list from {{ count }} items to {{ max }}</a
            >.
          </p>
        </div>

        <WrapWithHint
          class="has-text-centered mt-4"
          :content="!downloadLabelsValid ? 'Select recipes first' : null"
        >
          <div
            :style="{ cursor: !downloadLabelsValid ? 'not-allowed' : 'auto' }"
            @click="showBulkSelectionHint()"
          >
            <button
              id="download-labels"
              class="button"
              :class="{
                'is-success': downloadLabelsValid,
                'is-loading': isBusy,
                'is-outlined': !downloadLabelsValid,
                'click-disabled': !downloadLabelsValid,
                'half-opacity': !downloadLabelsValid,
              }"
              @click="handleDownloadLabels"
            >
              Download
            </button>
          </div>
        </WrapWithHint>
      </div>

      <!-- Download Data box -->
      <div v-if="enableDataDownload" class="box">
        <BulkDataDownloadPanel
          :selected-ids="recipeSearchStore.selection"
          :user-id="userId"
          :daily-values-options="dailyValuesOptions"
          :download-button-color-is-primary="false"
          button-container-class="mt-4"
        />
      </div>
    </div>
  </div>
</template>

<script setup>import { ref as _ref, computed as _computed } from 'vue';

  import RecipeSelector from "./recipe-selector.vue";
  import BulkActions from "./bulk-manage-recipes/bulk-actions.vue";
  import BulkDataDownloadPanel from "./bulk-manage-recipes/bulk-data-download-panel.vue";
  import WrapWithHint from "./wrap-with-hint.vue";

  import { Flash } from "~/jose_modern/flash.js";

  import { recipeLabelsBatchDownloadPath, recipeFilterBatchDownloadPath } from "~/routes";

  import { usePrintDialogStore } from "./stores/print-dialog-store.js";
  import { useRecipeSearchStore } from "./stores/recipe-search-store.js";
  import { useRecipeTagStore } from "./stores/recipe-tag-store.js";

  import { onBeforeMount } from "vue";
  import uniqid from "uniqid";

  const flashError = (msg) => Flash.error(msg);

  const props = defineProps({
    userId: { type: String, required: true },
    userHasArchivedRecipes: { type: Boolean, required: true },
    min: { type: Number, default: 1 },
    max: { type: Number, default: 2 ** 32 },

    // Important: if enableEdit/enableDataDownload, outer page must have #bulk-actions element (otherwise, expect some JS errors).
    enableEdit: { type: Boolean, default: false },
    enableDataDownload: { type: Boolean, default: false },

    enableArchiving: { type: Boolean, default: true },

    // Pass down from Ruby: `"daily-values-options": daily_values_flat_json`
    dailyValuesOptions: { type: Array, required: true },

    deleteConfirms: { type: Object, required: true },
  });

  const printDialogStore = usePrintDialogStore();

  const recipeSearchStore = useRecipeSearchStore("printDialog.recipeSearch");
  const recipeTagStore = useRecipeTagStore();

  onBeforeMount(() => {
    const loadedFromStorage = recipeSearchStore.userId !== null;

    recipeSearchStore.setUserId(props.userId);
    recipeSearchStore.setSearchEndpoint(recipeFilterBatchDownloadPath(props.userId));
    recipeSearchStore.assignInitial(
      { archived: false, empty: false },
      { patch: !loadedFromStorage }
    );

    recipeTagStore.setUserId(props.userId);
    recipeTagStore.loadAllTags();

    recipeSearchStore.loadInitial();
  });

  let isBusy = _ref(false);

  function showBulkSelectionHint() {
    if (!downloadLabelsValid.value) {
      document.dispatchEvent(new CustomEvent("recipe-bulk-selection:hint"));
    }
  }

  /* unique LABEL values */
  const [fileFormatId, pageSizeId, zoomId, orientationId] = [
    uniqid(),
    uniqid(),
    uniqid(),
    uniqid(),
  ];

  const count = _computed(() => recipeSearchStore.selection.length || 0);
  const downloadLabelsTitle = _computed(() => {
    return count.value > 1 ? `Download ${count.value} Labels` : "Download Labels";
  });
  const downloadLabelsValid = _computed(() => count.value >= props.min && count.value <= props.max);
  const downloadLabelsTooMany = _computed(() => count.value > props.max);

  function truncateSelection() {
    recipeSearchStore.selection = recipeSearchStore.selection.slice(0, props.max);
  }

  async function handleDownloadLabels() {
    isBusy.value = true;
    let statusUrl = undefined;
    const errorMessage = "Failed to download labels.";
    try {
      const response = await fetch(recipeLabelsBatchDownloadPath(props.userId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({
          "stashed-file-client-id": uniqid(),
          "recipe_ids": recipeSearchStore.selection,
          "archive_format": printDialogStore.archiveFormat,
          "inner_format": printDialogStore.innerFormat,
          "page_size": printDialogStore.pageSize,
          "orientation": printDialogStore.orientation,
          "zoom": printDialogStore.zoom,
        }),
      });
      const json = await response.json();
      statusUrl = json.status_url;
      if (statusUrl) {
        window.location.assign(statusUrl);
      }
    } catch (err) {
      console.error(err);
      flashError(errorMessage);
    }
    if (!statusUrl) {
      flashError(errorMessage);
    }
    isBusy.value = false;
  }
</script>

<style lang="scss" scoped>
  @import "~jose_modern/css";

  .mobile-help {
    display: none;

    @include until($desktop) {
      display: flex;
      gap: 0.25em;
    }
  }
</style>
